
import { defineComponent, computed, ref, onMounted } from "vue";
import NativeClient, { TitleInfo } from "@/client/native-client";
import store from "@/store";
import { useApiErrorHandler } from "@/use/use-api-error-handler";
import { useApiErrorDialog } from "@/use/use-api-error-dialog";
import { useLog } from "@/use/use-log";
import { PAGE_ID } from "@/const/log-const";
import { ApiError, BeaconLogInfo, UpdatePageInfo } from "@/types/common";
import { COMMON_LIMIT_NUM } from "@/const/limit_const";
import { COMMON_MESSAGE_STRING } from "@/const/message_const";
import CommonUtil from "@/utils/common-utils";
import CommonDesignDialog from "@/components/common/CommonDesignDialog.vue";
import {
  FridgeDetailsReductionAmountListItem,
  FridgeDetailsReductionAmountList,
  FridgeDetailsDisplayDescriptionList 
} from "@/types/fridgeDetails";

export default defineComponent({
name: "FridgeReductionInfoPage",
components: { CommonDesignDialog },
setup() {
    const { setLogId, outputPageLogs } = useLog();

    // タイトル行の設定
    NativeClient.setTitle(new TitleInfo(false, "電気代削減額"));
    CommonUtil.scroll({ top: 0, left: 0 });

    // 表示更新のためのリアクティブ
    const reductionInfo = computed(() => {
      const reductionInfo: FridgeDetailsReductionAmountList | null = store.getters["fridgeDetails/getReductionInfo"];
      return reductionInfo;
    });

    // 月表示（選択）
    const selectIndex = ref(0);
    const dateType = 2;
    const monthList = computed(() => {
      const reductionInfo: FridgeDetailsReductionAmountList | null = store.getters["fridgeDetails/getReductionInfo"];
      if (reductionInfo == null || reductionInfo.reduction_amounts_list.length == 0) {
        NativeClient.sendDebugLog("FridgeReductionInfoPage monthList is no data");
        return [];
      }
      // 月のみ抽出
      NativeClient.sendDebugLog("FridgeReductionInfoPage monthList reductionInfo= " + JSON.stringify(reductionInfo));
      const yearMonthList = reductionInfo.reduction_amounts_list.map(element => element.date);
      yearMonthList.sort();
      const firstMonth = yearMonthList[0];
      const lastMonth = yearMonthList[yearMonthList.length - 1];
      const validMonth = `${firstMonth.substring(0, 4)}年${parseInt(firstMonth.substring(5, 7))}月〜${lastMonth.substring(0, 4)}年${parseInt(lastMonth.substring(5, 7))}月`;
      const monthList: Array<string> = [];
      monthList.push(validMonth);
      NativeClient.sendDebugLog("FridgeReductionInfoPage monthList = " + JSON.stringify(monthList));
      return monthList;
    });
    const isDisableButtonHidden = true;

    // 文言
    const getDescriptionString = (sectionId: string) => {
      const descriptionList: FridgeDetailsDisplayDescriptionList | null = store.getters["fridgeDetails/getDisplayDescriptionList"];
      if (descriptionList == null ||
          descriptionList.screen_list == null ||
          descriptionList.screen_list == undefined ||
          descriptionList.screen_list.length == 0) {
        return "";
      }
      const foundDescription = descriptionList.screen_list.find(element => element.screen_id == "V101M");
      if (foundDescription == undefined) {
        return "";
      }
      const foundString = foundDescription.display_value_list.find(element => element.section_id == sectionId);
      if (foundString == undefined) {
        return "";
      }
      return foundString.contents;
    };
    const reductionNoteText = computed(() => {
      return getDescriptionString("001");
    });
    const footNoteText = computed(() => {
      return getDescriptionString("002");
    });

    // 定数
    const previousMonthText = "先月";
    const currentMonthText = "今月";
    const previousYearMonthText = "前年同月";
    const priceUnitText = "円";

    // グラフ描画
    const baseCanvasSize = { width: 320, height: 240 };
    const canvasSize = computed(() => {
      const scale = window.devicePixelRatio;
      NativeClient.sendDebugLog("FridgeReductionInfoPage devicePixelRatio = " + scale);
      return { width: baseCanvasSize.width * scale, height: baseCanvasSize.height * scale };
    });
    const canvas = ref<HTMLCanvasElement>();
    const calcMaxValue = (list: Array<FridgeDetailsReductionAmountListItem>) => {
      // 回数のMax値を求める
      let maxValue = 0;
      if (list.length > 0) {
        maxValue = list.map(element => element.reduction_amount).reduce((a, b) => Math.max(a, b), -Infinity);
        if (maxValue > 200) {
          // 600超えるならば。600にしておく
          maxValue = 200;
        }
      }
      return maxValue;
    };
    const calsYAxisScales = (list: Array<FridgeDetailsReductionAmountListItem>) => {
      const maxValue = calcMaxValue(list);
      let retValue: Array<number> = [];
      if (0 <= maxValue && maxValue <= 20) {
        retValue = [0, 10, 20];
      } else if (20 < maxValue && maxValue <= 50) {
        retValue = [0, 25, 50];
      } else if (50 < maxValue && maxValue <= 100) {
        retValue = [0, 50, 100];
      } else if (100 < maxValue) {
        retValue = [0, 100, 200];
      }
      return retValue;
    };
    const drawGraph = () => {
      NativeClient.sendDebugLog("FridgeReductionInfoPage drawGraph start");
      const reductionInfo: FridgeDetailsReductionAmountList | null = store.getters["fridgeDetails/getReductionInfo"];
      if (reductionInfo == null || reductionInfo.reduction_amounts_list.length == 0) {
        // データが存在しないので描かない
        return;
      }
      if (canvas.value === undefined) {
        NativeClient.sendDebugLog("FridgeReductionInfoPage drawGraph canvas.value is undefined");
        return;
      }
      const targetContext = canvas.value.getContext("2d");
      if (targetContext == null) {
        NativeClient.sendDebugLog("FridgeDoorMonitorDailyItem drawGraph targetContext is null");
        return;
      }
      const scales = calsYAxisScales(reductionInfo.reduction_amounts_list);
      const maxValue = Math.max(...scales);
      if (maxValue <= 0) {
        // 配列無なので描画できない
        NativeClient.sendDebugLog("FridgeReductionInfoPage no maxValue");
        return;
      }

      const screenScale = window.devicePixelRatio;
      NativeClient.sendDebugLog("FridgeReductionInfoPage drawGraph = " + screenScale);
      const realCanvasSize = { width: baseCanvasSize.width * screenScale, height: baseCanvasSize.height * screenScale };
      targetContext.clearRect(0, 0, realCanvasSize.width, realCanvasSize.height);

      // 定数設定
      const originPosition = { x: 20 * screenScale, y: realCanvasSize.height - 16 * screenScale };
      const barWidth = 8 * screenScale;
      const startXAxis = 14 * screenScale + originPosition.x;
      const intervalXAxis = 14 * screenScale;
      const maxBarHeight = 200 * screenScale;
      const scaleTop = 10 * screenScale;
      const strokeColor = "#707070";
      const lastYearFillStyle = "rgba(172,172,172,1.0)";
      const thisYearFillStyle = "rgba(247,136,29,1.0)";
      const scaleLineColor = "rgba(179,179,179,0.3)"
      const lineWidth = screenScale;

      // Y軸
      targetContext.beginPath();
      targetContext.moveTo(originPosition.x, originPosition.y);
      targetContext.lineTo(originPosition.x, scaleTop);
      targetContext.strokeStyle = strokeColor;
      targetContext.lineWidth = lineWidth;
      targetContext.stroke();
      NativeClient.sendDebugLog("FridgeReductionInfoPage drawGraph Y axis start = (" + originPosition.x + ", " + originPosition.y + "), end = (" + originPosition.x + ", " + 0 + ")");

      // X軸
      targetContext.beginPath();
      targetContext.moveTo(originPosition.x, originPosition.y);
      targetContext.lineTo(realCanvasSize.width - 20 * screenScale, originPosition.y);
      targetContext.strokeStyle = strokeColor;
      targetContext.lineWidth = lineWidth;
      targetContext.stroke();

      // 最大のスケール線
      targetContext.beginPath();
      targetContext.moveTo(originPosition.x, originPosition.y - maxBarHeight);
      targetContext.lineTo(realCanvasSize.width - 20 * screenScale, originPosition.y - maxBarHeight);
      targetContext.strokeStyle = scaleLineColor;
      targetContext.setLineDash([]);
      targetContext.lineWidth = lineWidth;
      targetContext.stroke();

      // 3/4のスケール線
      targetContext.beginPath();
      targetContext.moveTo(originPosition.x, originPosition.y - 3 * maxBarHeight / 4);
      targetContext.lineTo(realCanvasSize.width - 20 * screenScale, originPosition.y - 3 * maxBarHeight / 4);
      targetContext.strokeStyle = scaleLineColor;
      targetContext.setLineDash([2, 2]);
      targetContext.lineWidth = lineWidth;
      targetContext.stroke();

      // 2/4のスケール線
      targetContext.beginPath();
      targetContext.moveTo(originPosition.x, originPosition.y - maxBarHeight / 2);
      targetContext.lineTo(realCanvasSize.width - 20 * screenScale, originPosition.y - maxBarHeight / 2);
      targetContext.strokeStyle = scaleLineColor;
      targetContext.setLineDash([]);
      targetContext.lineWidth = lineWidth;
      targetContext.stroke();

      // 1/4のスケール線
      targetContext.beginPath();
      targetContext.moveTo(originPosition.x, originPosition.y - maxBarHeight / 4);
      targetContext.lineTo(realCanvasSize.width - 20 * screenScale, originPosition.y - maxBarHeight / 4);
      targetContext.strokeStyle = scaleLineColor;
      targetContext.setLineDash([2, 2]);
      targetContext.lineWidth = lineWidth;
      targetContext.stroke();

      // 描画
      const date = new Date();
      const currentYear = String(date.getFullYear());
      let currentXPosition = startXAxis;
      for (let i = 1; i <= 12; i ++) {
        const currentMonth = ('0' + i).slice(-2);
        const findIndex = reductionInfo.reduction_amounts_list.findIndex(element => element.date.substring(5, 7) == currentMonth);
        let drawHeight = 0;
        if (findIndex >= 0) {
          // 存在する
          drawHeight = reductionInfo.reduction_amounts_list[findIndex].reduction_amount / maxValue * maxBarHeight;
          if (drawHeight > maxBarHeight) {
            drawHeight = maxBarHeight;
          }

          const existYear = reductionInfo.reduction_amounts_list[findIndex].date.indexOf(currentYear);
          NativeClient.sendDebugLog("FridgeReductionInfoPage drawGraph thisYear = " + currentYear + ", date = " + reductionInfo.reduction_amounts_list[findIndex].date);
          if (existYear !== -1) {
            targetContext.fillStyle = thisYearFillStyle;
          } else {
            targetContext.fillStyle = lastYearFillStyle;
          }
          targetContext.fillRect(currentXPosition, originPosition.y - drawHeight, barWidth, drawHeight);
        }

        // 次の位置へ
        currentXPosition = currentXPosition + barWidth + intervalXAxis;
      }
    };

    // グラフのスケール
    const yAxisTitle = "(円)";
    const xAxisTitle = "(月)";
    const yAxisScales = computed(() => {
      NativeClient.sendDebugLog("FridgeReductionInfoPage yAxisScales start");
      const reductionInfo: FridgeDetailsReductionAmountList | null = store.getters["fridgeDetails/getReductionInfo"];
      if (reductionInfo == null || reductionInfo.reduction_amounts_list.length == 0) {
        NativeClient.sendDebugLog("FridgeReductionInfoPage yAxisScales no data");
        return;
      }
      const realScales = calsYAxisScales(reductionInfo.reduction_amounts_list);
      if (realScales.length != 3) {
        NativeClient.sendDebugLog("FridgeReductionInfoPage yAxisScales invalid calsYAxisScales");
        return;
      }

      // スケールの個数は、レイアウトの関係から七つ必要
      const retvalue: Array<string> = [];
      retvalue.push(String(realScales[0]));
      retvalue.push("");
      retvalue.push("");
      retvalue.push(String(realScales[1]));
      retvalue.push("");
      retvalue.push("");
      retvalue.push(String(realScales[2]));
      return retvalue;
    });
    const xAxisScales = [ "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12" ];

    //
    // API実行
    //
    const getReductionInfo = async () => {
      NativeClient.sendDebugLog("FridgeReductionInfoPage getReductionInfo()");
      store.dispatch("common/startLoading");
      await NativeClient.getApiParams();
      await store.dispatch("fridgeDetails/getReductionInfo");
      store.dispatch("common/stopLoading");
      drawGraph();
    };
    const getDescription = async () => {
      store.dispatch("common/startLoading");
      await store.dispatch("fridgeDetails/getDisplayDescriptionList");
      store.dispatch("common/stopLoading");
    };

    // 表示の制御
    //let isDisplayed = false;
    onMounted(() => {
      NativeClient.sendDebugLog("FridgeReductionInfoPage onMounted()");

      // ページIDは未決定
      // const logInfo = new BeaconLogInfo(PAGE_ID.FAVORITES_MYLIST, null, []);
      // NativeClient.sendViewedLog(logInfo);
      getReductionInfo();
      getDescription();
    });

    // 画面が小さい場合の調整
    const styleGraphArea = computed(() => {
      if (window.innerWidth <= 320) {
        return { 'transform': 'scale(0.9)' };
      }
      return { 'transform': 'scale(1.0)' };
    });

    //
    // 金額の表示
    //
    const previousMonthPriceText = computed(() => {
      const reductionInfo: FridgeDetailsReductionAmountList | null = store.getters["fridgeDetails/getReductionInfo"];
      if (reductionInfo == null || reductionInfo.lastmonth_reduction_amount == null) {
        return "ー";
      }
      return String(reductionInfo.lastmonth_reduction_amount);
    });
    const currentMonthPriceText = computed(() => {
      const reductionInfo: FridgeDetailsReductionAmountList | null = store.getters["fridgeDetails/getReductionInfo"];
      if (reductionInfo == null || reductionInfo.current_reduction_amount == null) {
        return "ー";
      }
      return String(reductionInfo.current_reduction_amount);
    });
    const previousYearMonthPriceText = computed(() => {
      const reductionInfo: FridgeDetailsReductionAmountList | null = store.getters["fridgeDetails/getReductionInfo"];
      if (reductionInfo == null || reductionInfo.lastyear_reduction_amount == null) {
        return "ー";
      }
      return String(reductionInfo.lastyear_reduction_amount);
    });

    //
    // APIリクエスト時のエラーハンドル
    //
    const {
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage
    } = useApiErrorHandler();
    const { closeDialog } = useApiErrorDialog();
    const typeApiError = ref(9);
    const retryButtonTitle = "リトライ";
    const okButtonTitle = "OK";
    // APIのエラー・ダイアログは、リトライが存在しないが、共通ダイアログのIFは残っているため、削除せず、そのまま置いておく
    const retryApiErrorHandler = () => {
      closeDialog();
      // getReductionInfo();
    };
    const okApiErrorHandler = () => {
      closeDialog();
    };

    return {
      selectIndex,
      dateType,
      isDisableButtonHidden,
      monthList,
      reductionInfo,
      styleGraphArea,
      canvas,
      canvasSize,
      yAxisTitle,
      xAxisTitle,
      yAxisScales,
      xAxisScales,
      previousMonthText,
      currentMonthText,
      previousYearMonthText,
      priceUnitText,
      previousMonthPriceText,
      currentMonthPriceText,
      previousYearMonthPriceText,
      reductionNoteText,
      footNoteText,
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage,
      typeApiError,
      retryButtonTitle,
      okButtonTitle,
      retryApiErrorHandler,
      okApiErrorHandler
    };
  }
});
